import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useMemo, useCallback } from 'react';
import { listToMap, isDefined, unique, _cs } from '@togglecorp/fujs';
import { IoCheckmark } from '../../node_modules/react-icons/io5/index.esm.js';
import ElementFragments from '../ElementFragments/index.js';
import SelectInputContainer from '../SelectInputContainer/index.js';
import { genericMemo } from '../../utils/index.js';
import styles from './styles.css.js';

function Option(props) {
  const {
    children,
    ellipsize = false
  } = props;
  return /*#__PURE__*/React.createElement(ElementFragments, {
    icons: /*#__PURE__*/React.createElement(IoCheckmark, {
      className: styles.icon
    }),
    childrenContainerClassName: styles.label
  }, /*#__PURE__*/React.createElement("div", {
    className: _cs(styles.text, ellipsize && styles.ellipsis)
  }, children));
}

const MemoizedOption = genericMemo(Option);
const emptyList = [];

function SearchSelectInput(props) {
  const {
    keySelector,
    labelSelector,
    optionLabelSelector,
    name,
    onChange,
    onOptionsChange,
    options: optionsFromProps,
    optionsPending,
    value,
    sortFunction,
    searchOptions: searchOptionsFromProps,
    onSearchValueChange,
    onShowDropdownChange,
    ellipsizeOptions,
    selectedOptionsAtTop = true,
    handleShowMoreClick,
    ...otherProps
  } = props;
  const options = optionsFromProps ?? emptyList;
  const searchOptions = searchOptionsFromProps ?? emptyList;
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [focused, setFocused] = React.useState(false);
  const [focusedKey, setFocusedKey] = React.useState();
  const [selectedKeys, setSelectedKeys] = useState({});
  const optionsLabelMap = useMemo(() => listToMap(options, keySelector, labelSelector), [options, keySelector, labelSelector]);
  const valueDisplay = isDefined(value) ? optionsLabelMap[value] ?? '?' : ''; // NOTE: we can skip this calculation if optionsShowInitially is false

  const selectedOptions = useMemo(() => {
    const selectedValue = options === null || options === void 0 ? void 0 : options.find(item => keySelector(item) === value);
    return !selectedValue ? [] : [selectedValue];
  }, [value, options, keySelector]);
  const realOptions = useMemo(() => {
    const allOptions = searchInputValue ? searchOptions : unique([...searchOptions, ...selectedOptions], keySelector);

    if (!selectedOptionsAtTop) {
      return sortFunction ? sortFunction(allOptions, searchInputValue, labelSelector) : allOptions;
    }

    const initiallySelected = allOptions.filter(item => selectedKeys[keySelector(item)]);
    const initiallyNotSelected = allOptions.filter(item => !selectedKeys[keySelector(item)]);

    if (sortFunction) {
      return [...sortFunction(initiallySelected, searchInputValue, labelSelector), ...sortFunction(initiallyNotSelected, searchInputValue, labelSelector)];
    }

    return [...initiallySelected, ...initiallyNotSelected];
  }, [keySelector, labelSelector, searchInputValue, searchOptions, selectedKeys, selectedOptions, sortFunction, selectedOptionsAtTop]);
  const handleSearchValueChange = useCallback(searchValue => {
    setSearchInputValue(searchValue);

    if (onSearchValueChange) {
      onSearchValueChange(searchValue);
    }
  }, [onSearchValueChange]);
  const handleChangeDropdown = useCallback(myVal => {
    if (onShowDropdownChange) {
      onShowDropdownChange(myVal);
    }

    if (myVal) {
      setSelectedKeys(listToMap(value ? [value] : [], item => item, () => true));
      setFocusedKey(value ? {
        key: value
      } : undefined);
    } else {
      setSelectedKeys({});
      setFocusedKey(undefined);
      setSearchInputValue('');

      if (onSearchValueChange) {
        onSearchValueChange('');
      }
    }
  }, [value, onSearchValueChange, onShowDropdownChange]);
  const optionRendererParams = useCallback((key, option) => {
    const isActive = key === value;
    return {
      children: optionLabelSelector ? optionLabelSelector(option) : labelSelector(option),
      containerClassName: _cs(styles.option, isActive && styles.active),
      title: labelSelector(option),
      ellipsize: ellipsizeOptions
    };
  }, [value, labelSelector, optionLabelSelector, ellipsizeOptions]);
  const handleOptionClick = useCallback((k, v) => {
    if (onOptionsChange) {
      onOptionsChange(existingOptions => {
        const safeOptions = existingOptions ?? [];
        const opt = safeOptions.find(item => keySelector(item) === k);

        if (opt) {
          return existingOptions;
        }

        return [...safeOptions, v];
      });
    }

    onChange(k, name);
  }, [onChange, name, onOptionsChange, keySelector]);
  const handleClear = useCallback(() => {
    // eslint-disable-next-line react/destructuring-assignment
    if (!props.nonClearable) {
      // eslint-disable-next-line react/destructuring-assignment
      props.onChange(undefined, name);
    }
  }, // eslint-disable-next-line react/destructuring-assignment, react-hooks/exhaustive-deps
  [name, props.onChange, props.nonClearable]);
  return /*#__PURE__*/React.createElement(SelectInputContainer, _extends({}, otherProps, {
    name: name,
    options: realOptions,
    optionsPending: optionsPending,
    optionsFiltered: (searchInputValue === null || searchInputValue === void 0 ? void 0 : searchInputValue.length) > 0,
    optionKeySelector: keySelector,
    optionRenderer: MemoizedOption,
    optionRendererParams: optionRendererParams,
    optionContainerClassName: styles.optionContainer,
    onOptionClick: handleOptionClick,
    valueDisplay: valueDisplay,
    onClear: handleClear,
    searchText: searchInputValue,
    onSearchTextChange: handleSearchValueChange,
    onDropdownShownChange: handleChangeDropdown,
    focused: focused,
    onFocusedChange: setFocused,
    focusedKey: focusedKey,
    onFocusedKeyChange: setFocusedKey,
    hasValue: isDefined(value),
    persistentOptionPopup: false,
    handleShowMoreClick: handleShowMoreClick
  }));
}

var SearchSelectInput$1 = genericMemo(SearchSelectInput);

export { SearchSelectInput$1 as default };
//# sourceMappingURL=index.js.map
